import {
  canReadSetting,
  getSettingCatalogDefinitionsForGroup,
  SettingCatalogGroups,
} from "./index";
import {
  adjustFuseScoreForValueSemantics,
  createValueSemanticsSearchText,
  getQueryValueSemanticKinds,
  type QueryValueSemanticKind,
} from "./search-context";
import {
  adjustFuseScoreForProductArea,
  searchWeightedDocuments,
  stringifySearchContext,
  toSearchRelevanceScore,
} from "./search-index";
import type {
  SettingDefinition,
  SettingGroupChatSurface,
  SettingGroupDefinition,
  SettingGroupFullPageTarget,
  SettingProductArea,
  SettingVisibility,
} from "./types";

const DEFAULT_SETTINGS_SEARCH_VIEWER_VISIBILITY = "merchant";
const MAX_MATCHED_SETTINGS_PER_GROUP = 5;
const SETTINGS_SEARCH_KEYS = [
  { name: "id", weight: 3 },
  { name: "title", weight: 4 },
  { name: "description", weight: 2 },
  { name: "synonyms", weight: 3 },
  { name: "productArea", weight: 1 },
  { name: "groupTitle", weight: 2 },
  { name: "groupDescription", weight: 1 },
  { name: "groupSynonyms", weight: 2 },
  { name: "valueSemantics", weight: 1 },
  { name: "changeGuidance", weight: 1 },
  { name: "dependencies", weight: 1 },
  { name: "valueSource", weight: 1.5 },
] as const;

export interface SettingsSearchMatchedSetting {
  id: string;
  label: string;
  description: string;
  score: number;
}

export interface SettingsSearchGroupResult {
  id: string;
  title: string;
  description: string;
  productArea: SettingProductArea;
  fullPageTarget?: SettingGroupFullPageTarget;
  chatSurface?: SettingGroupChatSurface;
  settingCount: number;
  score: number;
  matchedSettings: SettingsSearchMatchedSetting[];
}

export interface SettingsSearchCatalogResult {
  query: string;
  groups: SettingsSearchGroupResult[];
}

type SettingsSearchDocument =
  | SettingsSearchGroupDocument
  | SettingsSearchSettingDocument;

interface SettingsSearchDocumentBase {
  id: string;
  title: string;
  description: string;
  synonyms?: readonly string[];
  productArea: SettingProductArea;
  group: SettingGroupDefinition;
  groupTitle: string;
  groupDescription: string;
  groupSynonyms?: readonly string[];
  definitions: readonly SettingDefinition[];
  valueSemantics?: string;
  changeGuidance?: string;
  dependencies?: string;
  valueSource?: string;
}

interface SettingsSearchGroupDocument extends SettingsSearchDocumentBase {
  kind: "group";
}

interface SettingsSearchSettingDocument extends SettingsSearchDocumentBase {
  kind: "setting";
  definition: SettingDefinition;
}

interface AggregatedSettingsSearchGroup {
  group: SettingGroupDefinition;
  definitions: readonly SettingDefinition[];
  bestFuseScore: number;
  matchedSettingsById: Map<string, SettingsSearchMatchedSetting>;
}

export function searchSettingsCatalog({
  query,
  productArea,
  limit,
  viewerVisibility = DEFAULT_SETTINGS_SEARCH_VIEWER_VISIBILITY,
}: {
  query: string;
  productArea?: SettingProductArea;
  limit: number;
  viewerVisibility?: SettingVisibility;
}): SettingsSearchCatalogResult {
  const sanitizedLimit = sanitizeSearchLimit(limit);
  const trimmedQuery = query.trim();
  if (!trimmedQuery || sanitizedLimit === 0) {
    return { query, groups: [] };
  }

  // Product area is a routing hint, not a hard filter. Some merchant-facing
  // settings are described in one product area but owned by another catalog.
  const searchDocuments = createSettingsSearchDocuments({ viewerVisibility });
  const querySemanticKinds = getQueryValueSemanticKinds(
    trimmedQuery.toLowerCase(),
  );

  const groupsById = new Map<string, AggregatedSettingsSearchGroup>();
  for (const result of searchWeightedDocuments({
    documents: searchDocuments,
    keys: SETTINGS_SEARCH_KEYS,
    query: trimmedQuery,
  })) {
    const document = result.document;
    const fuseScore = getSettingsDocumentFuseScore(
      document,
      result.score,
      productArea,
      querySemanticKinds,
    );
    const groupResult = getOrCreateAggregatedSearchGroup(
      groupsById,
      document.group,
      document.definitions,
    );
    groupResult.bestFuseScore = Math.min(groupResult.bestFuseScore, fuseScore);

    if (document.kind === "setting") {
      const metadata = document.definition.metadata;
      const matchedSetting = {
        id: document.definition.id,
        label: metadata.label,
        description: metadata.description,
        score: toSearchRelevanceScore(fuseScore),
      };
      const existing = groupResult.matchedSettingsById.get(matchedSetting.id);
      if (!existing || matchedSetting.score > existing.score) {
        groupResult.matchedSettingsById.set(matchedSetting.id, matchedSetting);
      }
    }
  }

  return {
    query,
    groups: [...groupsById.values()]
      .sort((a, b) => a.bestFuseScore - b.bestFuseScore)
      .slice(0, sanitizedLimit)
      .map((result) => ({
        id: result.group.id,
        title: result.group.title,
        description: result.group.description,
        productArea: result.group.productArea,
        fullPageTarget: result.group.fullPageTarget,
        chatSurface: result.group.chatSurface,
        settingCount: result.definitions.length,
        score: toSearchRelevanceScore(result.bestFuseScore),
        matchedSettings: [...result.matchedSettingsById.values()]
          .sort((a, b) => b.score - a.score)
          .slice(0, MAX_MATCHED_SETTINGS_PER_GROUP),
      })),
  };
}

function sanitizeSearchLimit(limit: number): number {
  if (!Number.isFinite(limit)) {
    return 0;
  }

  return Math.max(Math.floor(limit), 0);
}

function getSettingsDocumentFuseScore(
  document: SettingsSearchDocument,
  score: number | undefined,
  productArea: SettingProductArea | undefined,
  querySemanticKinds: ReadonlySet<QueryValueSemanticKind>,
): number {
  let fuseScore = score ?? 0;
  if (
    document.kind === "setting" &&
    document.definition.groupId !== document.group.id
  ) {
    fuseScore = Math.min(fuseScore + 0.08, 1);
  }
  if (document.kind === "setting") {
    fuseScore = adjustFuseScoreForValueSemantics({
      fuseScore,
      querySemanticKinds,
      valueSemantics: document.definition.metadata.valueSemantics,
    });
  }

  return adjustFuseScoreForProductArea({
    documentProductArea: document.productArea,
    fuseScore,
    primaryProductArea: document.group.productArea,
    productArea,
  });
}

function createSettingsSearchDocuments({
  viewerVisibility,
}: {
  viewerVisibility: SettingVisibility;
}): SettingsSearchDocument[] {
  return SettingCatalogGroups.flatMap((group) => {
    if (!canReadSetting(viewerVisibility, group.access)) {
      return [];
    }

    const definitions = getVisibleDefinitionsForGroup({
      groupId: group.id,
      viewerVisibility,
    });
    if (definitions.length === 0) {
      return [];
    }

    return [
      {
        kind: "group" as const,
        id: group.id,
        title: group.title,
        description: group.description,
        synonyms: group.synonyms,
        productArea: group.productArea,
        group,
        groupTitle: group.title,
        groupDescription: group.description,
        groupSynonyms: group.synonyms,
        definitions,
      },
      ...definitions.map((definition) => {
        const metadata = definition.metadata;
        return {
          kind: "setting" as const,
          id: definition.id,
          title: metadata.label,
          description: metadata.description,
          synonyms: metadata.synonyms,
          productArea: metadata.productArea,
          group,
          groupTitle: group.title,
          groupDescription: group.description,
          groupSynonyms: group.synonyms,
          definitions,
          definition,
          valueSemantics: createValueSemanticsSearchText(
            metadata.valueSemantics,
          ),
          changeGuidance: stringifySearchContext(metadata.changeGuidance),
          dependencies: stringifySearchContext(metadata.dependencies),
          valueSource: stringifySearchContext(definition.valueSource),
        };
      }),
    ];
  });
}

function getVisibleDefinitionsForGroup({
  groupId,
  viewerVisibility,
}: {
  groupId: string;
  viewerVisibility: SettingVisibility;
}): readonly SettingDefinition[] {
  return getSettingCatalogDefinitionsForGroup(groupId).filter((definition) => {
    return canReadSetting(viewerVisibility, definition.metadata.access);
  });
}

function getOrCreateAggregatedSearchGroup(
  groupsById: Map<string, AggregatedSettingsSearchGroup>,
  group: SettingGroupDefinition,
  definitions: readonly SettingDefinition[],
): AggregatedSettingsSearchGroup {
  const existing = groupsById.get(group.id);
  if (existing) {
    return existing;
  }

  const created = {
    group,
    definitions,
    bestFuseScore: Number.POSITIVE_INFINITY,
    matchedSettingsById: new Map<string, SettingsSearchMatchedSetting>(),
  };
  groupsById.set(group.id, created);
  return created;
}
